/**
 * Live updates (PRD NFR "Real-time"): one WebSocket to `/api/live` while signed in. Events are
 * invalidation hints only; on each one we refetch the affected queries through the normal API,
 * so permission checks (and confidentiality) stay on the server.
 */
import {
  LIVE_CLOSE_UNAUTHORIZED,
  LIVE_PATH,
  LIVE_PING,
  LIVE_PONG,
  type LiveEvent,
  parseLiveEvent,
} from '@hrms/shared'
import { type QueryClient, type QueryKey, useQueryClient } from '@tanstack/react-query'
import { useEffect, useSyncExternalStore } from 'react'
import { ME_KEY } from '../auth/AuthProvider.tsx'
import { CONFIG_KEY } from './config.ts'

export type LiveStatus = 'connecting' | 'live' | 'reconnecting' | 'offline'

/** Query keys refreshed by events (kept here so the ticket module needn't import the socket). */
const TICKETS: QueryKey = ['tickets']
const USERS: QueryKey = ['users']
const DIRECTORY: QueryKey = ['directory']
/** Mutations with this key hold back board refetches until they settle (no optimistic flicker). */
export const BOARD_MOVE_MUTATION: QueryKey = ['tickets', 'board', 'move']

const HEARTBEAT_MS = 25_000
/** No pong (or any message) this long after a ping: assume the connection is dead. */
const PONG_TIMEOUT_MS = 10_000
const BACKOFF_BASE_MS = 1_000
const BACKOFF_MAX_MS = 30_000
/** After this many failed attempts in a row the indicator says Offline instead of Reconnecting. */
const OFFLINE_AFTER = 4
/** Bursts of events (bulk saves) coalesce into one refetch per query. */
const FLUSH_MS = 120

// --- status store (read by the top-bar indicator) ---------------------------------------------
let status: LiveStatus = 'connecting'
const listeners = new Set<() => void>()
function setStatus(next: LiveStatus) {
  if (next === status) return
  status = next
  for (const l of listeners) l()
}
const subscribe = (l: () => void) => {
  listeners.add(l)
  return () => listeners.delete(l)
}

export function useLiveStatus(): LiveStatus {
  return useSyncExternalStore(
    subscribe,
    () => status,
    () => status,
  )
}

/** Which queries an event makes stale. Exported for tests. */
export function keysFor(event: LiveEvent): QueryKey[] {
  switch (event.type) {
    case 'ticket.changed':
      return [
        ['tickets', 'queue'],
        ['tickets', 'board'],
        ['tickets', 'mine'],
        ['tickets', 'detail', event.key.toUpperCase()],
      ]
    case 'config.changed':
      return [CONFIG_KEY]
    case 'users.changed':
      // Names and avatars show on tickets too; the signed-in user's own role may have changed.
      return [USERS, DIRECTORY, ME_KEY, TICKETS]
    default:
      return []
  }
}

/** Everything a missed event could have touched (after a reconnect). */
const RESYNC_KEYS: QueryKey[] = [TICKETS, CONFIG_KEY, USERS, DIRECTORY]

function createInvalidator(qc: QueryClient) {
  const pending = new Map<string, QueryKey>()
  let timer: number | undefined
  const flush = () => {
    timer = undefined
    const boardBusy = qc.isMutating({ mutationKey: BOARD_MOVE_MUTATION }) > 0
    for (const [id, key] of pending) {
      // An optimistic board move is in flight: refetch the board once it settles.
      if (boardBusy && key[0] === 'tickets' && (key[1] === 'board' || key.length === 1)) continue
      pending.delete(id)
      void qc.invalidateQueries({ queryKey: key })
    }
    if (pending.size > 0) timer = window.setTimeout(flush, 400)
  }
  return {
    add(keys: QueryKey[]) {
      for (const k of keys) pending.set(JSON.stringify(k), k)
      if (timer === undefined) timer = window.setTimeout(flush, FLUSH_MS)
    },
    dispose() {
      window.clearTimeout(timer)
      pending.clear()
    },
  }
}

function liveUrl(): string {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${proto}//${window.location.host}${LIVE_PATH}`
}

/**
 * Keeps the socket open while mounted (the signed-in app shell): exponential backoff with jitter
 * on drops, a heartbeat to notice dead connections, and a full resync after reconnecting.
 */
export function useLiveUpdates(enabled = true) {
  const qc = useQueryClient()
  useEffect(() => {
    if (!enabled) return
    const invalidate = createInvalidator(qc)
    let ws: WebSocket | null = null
    let attempts = 0
    let everConnected = false
    let stopped = false
    let retryTimer: number | undefined
    let heartbeat: number | undefined
    let pongTimer: number | undefined

    const clearTimers = () => {
      window.clearTimeout(retryTimer)
      window.clearInterval(heartbeat)
      window.clearTimeout(pongTimer)
      retryTimer = heartbeat = pongTimer = undefined
    }

    const connect = () => {
      if (stopped) return
      clearTimers()
      if (!navigator.onLine) {
        setStatus('offline')
        return
      }
      setStatus(
        attempts >= OFFLINE_AFTER ? 'offline' : everConnected ? 'reconnecting' : 'connecting',
      )
      const socket = new WebSocket(liveUrl())
      ws = socket
      socket.onopen = () => {
        if (ws !== socket) return
        const resync = everConnected || attempts > 0
        attempts = 0
        everConnected = true
        setStatus('live')
        // Anything could have changed while we were away.
        if (resync) invalidate.add(RESYNC_KEYS)
        heartbeat = window.setInterval(() => {
          if (socket.readyState !== WebSocket.OPEN) return
          socket.send(LIVE_PING)
          window.clearTimeout(pongTimer)
          pongTimer = window.setTimeout(() => socket.close(), PONG_TIMEOUT_MS)
        }, HEARTBEAT_MS)
      }
      socket.onmessage = (e) => {
        if (ws !== socket) return
        window.clearTimeout(pongTimer)
        if (e.data === LIVE_PONG) return
        const event = parseLiveEvent(String(e.data))
        if (event) invalidate.add(keysFor(event))
      }
      socket.onclose = (e) => {
        if (ws !== socket) return
        ws = null
        clearTimers()
        if (stopped) return
        if (e.code === LIVE_CLOSE_UNAUTHORIZED) {
          // Signed out elsewhere or archived: let the auth check decide (it signs out on 401).
          setStatus('offline')
          void qc.invalidateQueries({ queryKey: ME_KEY })
          return
        }
        attempts++
        // A refused handshake looks like any other drop; a stale session shows up as a 401 here.
        if (attempts >= 2) void qc.invalidateQueries({ queryKey: ME_KEY })
        setStatus(navigator.onLine && attempts < OFFLINE_AFTER ? 'reconnecting' : 'offline')
        const delay = Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** (attempts - 1))
        retryTimer = window.setTimeout(connect, delay * (0.8 + Math.random() * 0.4))
      }
    }

    const reconnectNow = () => {
      if (stopped || (ws && ws.readyState <= WebSocket.OPEN)) return
      attempts = Math.min(attempts, 1)
      connect()
    }
    const onOffline = () => {
      setStatus('offline')
      ws?.close()
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible') reconnectNow()
    }
    window.addEventListener('online', reconnectNow)
    window.addEventListener('offline', onOffline)
    document.addEventListener('visibilitychange', onVisible)
    connect()

    return () => {
      stopped = true
      clearTimers()
      invalidate.dispose()
      window.removeEventListener('online', reconnectNow)
      window.removeEventListener('offline', onOffline)
      document.removeEventListener('visibilitychange', onVisible)
      const socket = ws
      ws = null
      socket?.close()
      setStatus('connecting')
    }
  }, [qc, enabled])
}
